import * as THREE from 'three';
import { RESOURCE_DEFS } from './resources.js';
import { getGameState, saveGameState } from './state.js';
import { eventBus, GameEvents } from './events.js';
import { createResourceMesh } from '../meshes/resources.js';
import { RESOURCES } from './balance.js';
import { hasUpgrade } from './upgrades.js';

let scene = null;
let sandMesh = null;
const activeResources = new Map(); // resourceType -> [{ mesh, position }]
const raycaster = new THREE.Raycaster();
const exclusionZones = [];

// Perf: cached spawnable types array (avoid recreating every frame)
const spawnableTypes = Object.entries(RESOURCE_DEFS)
  .filter(([, d]) => d.spawnTimer)
  .map(([t]) => t);

export function addExclusionZone(position, radius) {
  exclusionZones.push({ position: position.clone(), radius });
}

export function initResourceSpawner(sceneRef, sandMeshRef) {
  scene = sceneRef;
  sandMesh = sandMeshRef;

  // Initialize tracking for each resource type
  for (const type of Object.keys(RESOURCE_DEFS)) {
    activeResources.set(type, []);
  }

  // Perform catch-up spawns for time elapsed since last session
  performCatchUpSpawns();

  // Place starter resources if the beach looks empty after catch-up
  performInitialSpawns();

  console.log('[ResourceSpawner] Initialized');
}

export function updateSpawnerSandMesh(newSandMesh) {
  sandMesh = newSandMesh;
}

/**
 * Calculate and queue catch-up spawns for resources that should have spawned
 * while the player was away. Prevents save-scumming by persisting spawn timers
 * and ensures returning players see resources that accumulated during absence.
 *
 * Caps: max 5 catch-up spawns per type, never exceeds maxVisible.
 * Staggers: spawns 2 resources per 100ms interval to avoid frame spikes.
 */
function performCatchUpSpawns() {
  const state = getGameState();
  const now = Date.now();
  const catchUpQueue = [];

  for (const [type, def] of Object.entries(RESOURCE_DEFS)) {
    if (!def.spawnTimer) continue;

    const lastSpawn = state.spawners.lastSpawnTimes[type] || 0;
    if (lastSpawn === 0) {
      // Never spawned — initialize timestamp so first spawn happens after timer
      state.spawners.lastSpawnTimes[type] = now;
      continue;
    }

    const elapsedSeconds = (now - lastSpawn) / 1000;
    const missedSpawns = Math.floor(elapsedSeconds / def.spawnTimer);

    if (missedSpawns > 0) {
      const currentCount = getVisibleCount(type);
      const available = def.maxVisible - currentCount;
      const toSpawn = Math.min(missedSpawns, available, 5);

      for (let i = 0; i < toSpawn; i++) {
        catchUpQueue.push(type);
      }

      // Update last spawn time to now
      state.spawners.lastSpawnTimes[type] = now;
    }
  }

  saveGameState(state);

  // Stagger catch-up spawns across frames (2 per 100ms interval)
  if (catchUpQueue.length > 0) {
    console.log(`[ResourceSpawner] Catch-up: queuing ${catchUpQueue.length} spawns`);
    let idx = 0;
    const staggerInterval = setInterval(() => {
      const batchSize = Math.min(2, catchUpQueue.length - idx);
      for (let i = 0; i < batchSize; i++) {
        spawnResource(catchUpQueue[idx]);
        idx++;
      }
      if (idx >= catchUpQueue.length) {
        clearInterval(staggerInterval);
      }
    }, 100);
  }
}

/**
 * Place starter resources on session start so the beach is never empty.
 * Runs after performCatchUpSpawns() — only tops up if catch-up didn't
 * already fill the minimums.
 */
function performInitialSpawns() {
  // Driftwood — always ensure at least 2 on the beach
  const driftwoodCount = getVisibleCount('driftwood');
  if (driftwoodCount < 2) {
    const needed = 2 - driftwoodCount;
    for (let i = 0; i < needed; i++) {
      spawnResource('driftwood');
    }
  }

  // Seashells — place 1-2 if fewer than 2 visible
  const seashellCount = getVisibleCount('seashell');
  if (seashellCount < 2) {
    const needed = Math.min(1 + Math.floor(Math.random() * 2), 2 - seashellCount);
    for (let i = 0; i < needed; i++) {
      spawnResource('seashell');
    }
  }

  // Coconuts — spawn 1-2 at random positions if fewer than 2 visible
  const coconutCount = getVisibleCount('coconut');
  if (coconutCount < 2) {
    const needed = Math.min(2 - coconutCount, 1 + Math.floor(Math.random() * 2));
    for (let i = 0; i < needed; i++) {
      spawnResource('coconut');
    }
  }

  console.log('[ResourceSpawner] Initial session spawns complete');
}

// Find a valid spawn position on the sand terrain
function findSpawnPosition(maxAttempts = 20) {
  for (let i = 0; i < maxAttempts; i++) {
    // Random position within island radius (~30 units from center)
    const angle = Math.random() * Math.PI * 2;
    const radius = 5 + Math.random() * 25; // 5-30 units from center
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;

    // Raycast down to find terrain height
    raycaster.set(new THREE.Vector3(x, 50, z), new THREE.Vector3(0, -1, 0));
    const hits = raycaster.intersectObject(sandMesh, true);

    if (hits.length === 0) continue; // off terrain

    const y = hits[0].point.y;
    if (y < 0.5) continue; // too close to water

    // Check spacing against other resources
    const pos = new THREE.Vector3(x, y, z);
    const minSpacing = RESOURCES.SPAWN_MIN_SPACING || 3;
    let tooClose = false;

    for (const [, resources] of activeResources) {
      for (const res of resources) {
        if (pos.distanceTo(res.mesh.position) < minSpacing) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) break;
    }

    // Check exclusion zones (trees, placed objects, etc.)
    let inExclusionZone = false;
    for (const zone of exclusionZones) {
      if (pos.distanceTo(zone.position) < zone.radius) {
        inExclusionZone = true;
        break;
      }
    }
    if (tooClose || inExclusionZone) continue;

    return pos;
  }

  return null; // failed to find valid position
}

// Spawn a single resource at a specific or random position
export function spawnResource(resourceType, position = null) {
  const def = RESOURCE_DEFS[resourceType];
  if (!def) return null;

  const resources = activeResources.get(resourceType);
  if (resources.length >= def.maxVisible) return null; // max cap reached

  const pos = position || findSpawnPosition();
  if (!pos) return null;

  const mesh = createResourceMesh(resourceType);
  mesh.position.copy(pos);
  // Compute bounding box to sit mesh on sand surface regardless of geometry shape
  const box = new THREE.Box3().setFromObject(mesh);
  const meshBottomLocal = box.min.y - mesh.position.y;
  mesh.position.y = pos.y - meshBottomLocal + 0.02;

  scene.add(mesh);
  resources.push({ mesh, spawnTime: Date.now() });

  eventBus.emit(GameEvents.RESOURCE_SPAWNED, {
    resourceType,
    position: { x: pos.x, y: pos.y, z: pos.z },
    mesh,
  });

  return mesh;
}

// Remove a resource from the world (called when collected)
export function removeResource(resourceType, mesh) {
  const resources = activeResources.get(resourceType);
  if (!resources) return;

  const idx = resources.findIndex(r => r.mesh === mesh);
  if (idx >= 0) {
    resources.splice(idx, 1);
    scene.remove(mesh);
    // Dispose geometry and material
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(m => m.dispose());
      } else {
        mesh.material.dispose();
      }
    }
    // Handle groups
    mesh.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }
}

// Get all active resource meshes (for interaction system)
export function getActiveResources() {
  return activeResources;
}

// Get count of visible resources by type
export function getVisibleCount(resourceType) {
  return (activeResources.get(resourceType) || []).length;
}

// Update function — call from game loop
// Checks respawn timers and spawns resources as needed (max 2 per frame)
export function updateResourceSpawner() {
  if (!scene || !sandMesh) return;

  const state = getGameState();
  const now = Date.now();
  let spawnsThisFrame = 0;
  const MAX_SPAWNS_PER_FRAME = 2;

  const oceanCurrentActive = hasUpgrade('ocean_current');

  for (const [type, def] of Object.entries(RESOURCE_DEFS)) {
    if (spawnsThisFrame >= MAX_SPAWNS_PER_FRAME) break;
    if (!def.spawnTimer) continue; // this type doesn't naturally spawn

    // Ocean Current upgrade halves driftwood spawn timer (doubles spawn rate)
    const effectiveTimer = (oceanCurrentActive && type === 'driftwood')
      ? def.spawnTimer / 2
      : def.spawnTimer;

    const lastSpawn = state.spawners.lastSpawnTimes[type] || 0;
    const elapsed = (now - lastSpawn) / 1000; // seconds

    if (elapsed >= effectiveTimer) {
      const resources = activeResources.get(type);
      if (resources.length < def.maxVisible) {
        const mesh = spawnResource(type);
        if (mesh) {
          state.spawners.lastSpawnTimes[type] = now;
          spawnsThisFrame++;
        }
      }
    }
  }

  // Ocean Current: spawn a random resource at the water edge every 60s
  if (oceanCurrentActive && spawnsThisFrame < MAX_SPAWNS_PER_FRAME) {
    const lastWaterSpawn = state.spawners.lastSpawnTimes['_water_edge'] || 0;
    const waterElapsed = (now - lastWaterSpawn) / 1000;
    if (waterElapsed >= 60) {
      // Pick a random resource type that naturally spawns
      const randomType = spawnableTypes[Math.floor(Math.random() * spawnableTypes.length)];
      const mesh = spawnResource(randomType);
      if (mesh) {
        state.spawners.lastSpawnTimes['_water_edge'] = now;
        spawnsThisFrame++;
      }
    }
  }

  if (spawnsThisFrame > 0) {
    saveGameState(state);
  }
}
